import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Worker, Viewer, SpecialZoomLevel } from '@react-pdf-viewer/core';
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import { pageNavigationPlugin } from '@react-pdf-viewer/page-navigation';
import { printPlugin } from '@react-pdf-viewer/print';

// Import styles
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/zoom/lib/styles/index.css';
import '@react-pdf-viewer/page-navigation/lib/styles/index.css';
import '@react-pdf-viewer/print/lib/styles/index.css';

import publicAxios from '../../../../services/PublicAxiosInstance.js';
import { getApiUrl } from '../../../../utils/ApiUtils.js';
import { toast } from 'sonner';
import {
    CheckCircle,
    XCircle,
    Clock,
    FileText,
    DollarSign,
    Calendar,
    Building,
    User,
    Mail,
    Phone,
    MapPin,
    Download,
    Eye,
    AlertTriangle,
    Shield,
    ExternalLink
} from 'lucide-react';

const PublicQuoteView = () => {
    const { companyId, token } = useParams();

    const [quote, setQuote] = useState(null);
    const [company, setCompany] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [customerInfo, setCustomerInfo] = useState({
        name: '',
        email: '',
        phone: '',
        company: ''
    });
    const [showCustomerForm, setShowCustomerForm] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);

    // PDF viewer plugins
    const zoomPluginInstance = zoomPlugin();
    const { ZoomInButton, ZoomOutButton, ZoomPopover } = zoomPluginInstance;

    const pageNavigationPluginInstance = pageNavigationPlugin();
    const { GoToNextPageButton, GoToPreviousPageButton, CurrentPageInput, NumberOfPages } = pageNavigationPluginInstance;

    const printPluginInstance = printPlugin();
    const { PrintButton } = printPluginInstance;

    console.log('PublicQuoteView mounted with token:', token);
    console.log('PublicQuoteView Company ID:', companyId);

    useEffect(() => {
        console.log('PublicQuoteView mounted with token:', token);
        fetchQuoteDetails();
    }, [token]);

    const fetchQuoteDetails = async () => {
        try {
            setLoading(true);
            const apiUrl = getApiUrl(`${companyId}/public/quote/view/${token}`);
            console.log('Fetching quote from URL:', apiUrl);

            const response = await publicAxios.get(apiUrl);

            if (response.data.success) {
                setQuote(response.data.quote);
                setCompany(response.data.company);

                // Generate PDF URL for embedding
                const pdfApiUrl = getApiUrl(`${companyId}/public/quote/pdf/${token}`);
                setPdfUrl(pdfApiUrl);

                // Pre-populate customer info if available
                if (response.data.quote.customer) {
                    setCustomerInfo({
                        name: response.data.quote.customer.name || '',
                        email: response.data.quote.customer.email || '',
                        phone: response.data.quote.customer.phone || '',
                        company: response.data.quote.customer.company_name || ''
                    });
                }
            } else {
                setError('Offerten kunde inte hittas eller har gått ut.');
            }
        } catch (error) {
            console.error('Error fetching quote:', error);
            if (error.response?.status === 404) {
                setError('Offerten kunde inte hittas.');
            } else if (error.response?.status === 410) {
                setError('Denna offert har gått ut eller är inte längre tillgänglig.');
            } else if (error.response?.status === 403) {
                setError('Du har inte behörighet att visa denna offert.');
            } else {
                setError('Ett fel uppstod när offerten skulle hämtas.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleQuoteAction = async (action) => {
        // Show customer info form for acceptance
        if (action === 'accept' && !showCustomerForm) {
            setShowCustomerForm(true);
            return;
        }

        // Validate customer info for acceptance
        if (action === 'accept') {
            if (!customerInfo.name || !customerInfo.email) {
                toast.warning('Vänligen fyll i namn och e-post');
                return;
            }
        }

        try {
            setActionLoading(true);

            const response = await publicAxios.post(getApiUrl(`${companyId}/public/quote/action/${token}`), {
                action,
                customer_info: action === 'accept' ? customerInfo : null
            });

            console.log('Quote action response:', response.data);

            if (response.data.success) {
                if (action === 'accept') {
                    toast.success('Offerten har accepterats. Vi kommer att kontakta dig inom kort');
                    if (response.data.invoice_created) {
                        toast.info('En faktura har automatiskt skapats baserat på den accepterade offerten');
                    }
                } else {
                    toast.info('Offerten har markerats som avvisad');
                }

                // Update quote status
                setQuote(prev => ({ ...prev, status: action === 'accept' ? 'Accepted' : 'Rejected' }));
                setShowCustomerForm(false);
            }
        } catch (error) {
            console.error('Error handling quote action:', error);
            toast.error('Ett fel uppstod. Vänligen försök igen');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDownloadPDF = async () => {
        try {
            const response = await publicAxios.get(getApiUrl(`${companyId}/public/quote/pdf/${token}`), {
                responseType: 'blob'
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Offert_${quote?.friendly_id || quote?.quote_number}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading PDF:', error);
            toast.error('Kunde inte ladda ner PDF:en. Vänligen försök igen');
        }
    };

    const calculateTotal = () => {
        // Use backend-calculated totals if available, otherwise calculate
        if (quote?.totals?.grand_total !== undefined) {
            return quote.totals.grand_total;
        }

        if (!quote?.items) return 0;

        return quote.items.reduce((total, item) => {
            const itemTotal = (item.unitprice * item.quantity);
            const taxRate = parseFloat(item.tax_rate?.toString().replace('%', '')) || 0;
            const taxAmount = itemTotal * (taxRate / 100);
            return total + itemTotal + taxAmount;
        }, 0);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('sv-SE', {
            style: 'currency',
            currency: quote?.currency || 'SEK'
        }).format(amount);
    };

    if (loading) {
        return (
            <div className="min-vh-100 d-flex justify-content-center align-items-center bg-light">
                <div className="text-center">
                    <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                        <span className="visually-hidden">Laddar...</span>
                    </div>
                    <h5 className="text-muted">Laddar offert...</h5>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-vh-100 d-flex justify-content-center align-items-center bg-light">
                <div className="text-center">
                    <div className="mb-4">
                        <AlertTriangle size={64} className="text-warning mb-3" />
                        <h3 className="h4 text-dark mb-3">Offerten kunde inte visas</h3>
                        <p className="text-muted mb-4">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    const isExpired = new Date(quote?.valid_until) < new Date();
    const canRespond = quote?.status === 'Sent' && !isExpired;
    const daysUntilExpiry = Math.ceil((new Date(quote?.valid_until) - new Date()) / (1000 * 60 * 60 * 24));

    return (
        <>
            <style>{`
                .public-quote-container {
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 2rem 1rem;
                    background: #f8f9fa;
                    min-height: 100vh;
                }

                .quote-header-card {
                    background: white;
                    border: 1px solid #dee2e6;
                    border-radius: 8px;
                    padding: 2rem;
                    margin-bottom: 1.5rem;
                    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
                }

                .quote-title {
                    font-size: 1.75rem;
                    font-weight: 600;
                    margin-bottom: 0.5rem;
                    color: #212529;
                }

                .quote-subtitle {
                    color: #6c757d;
                    font-size: 1rem;
                    margin-bottom: 1rem;
                }

                .header-content {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    gap: 2rem;
                    margin-bottom: 1.5rem;
                }

                .header-info {
                    flex: 1;
                }

                .header-actions {
                    display: flex;
                    gap: 0.75rem;
                    flex-shrink: 0;
                }

                .status-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.5rem 1rem;
                    border-radius: 6px;
                    font-weight: 500;
                    font-size: 0.875rem;
                    margin-bottom: 1rem;
                }

                .status-accepted { 
                    background: #d1edff; 
                    color: #004085; 
                    border: 1px solid #b6d7ff; 
                }
                .status-rejected { 
                    background: #f8d7da; 
                    color: #721c24; 
                    border: 1px solid #f5c6cb; 
                }
                .status-expired { 
                    background: #e2e3e5; 
                    color: #383d41; 
                    border: 1px solid #d6d8db; 
                }
                .status-pending { 
                    background: #fff3cd; 
                    color: #856404; 
                    border: 1px solid #ffeaa7; 
                }

                .header-action-button {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.75rem 1.25rem;
                    background: linear-gradient(135deg, #fff 0%, #f8fafc 100%);
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    text-decoration: none;
                    color: #374151;
                    font-weight: 500;
                    font-size: 0.875rem;
                    transition: all 0.2s ease;
                    cursor: pointer;
                    white-space: nowrap;
                }

                .header-action-button:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
                    border-color: #3b82f6;
                    color: #3b82f6;
                    text-decoration: none;
                }

                .header-action-button-success {
                    background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
                    border-color: #10b981;
                    color: #059669;
                }

                .header-action-button-success:hover {
                    border-color: #059669;
                    color: #047857;
                    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.15);
                }

                .header-action-button-danger {
                    background: linear-gradient(135deg, #fef2f2 0%, #fecaca 100%);
                    border-color: #ef4444;
                    color: #dc2626;
                }

                .header-action-button-danger:hover {
                    border-color: #dc2626;
                    color: #b91c1c;
                    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.15);
                }

                .stats-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 1rem;
                    margin-bottom: 1.5rem;
                }

                .stat-card {
                    background: white;
                    border: 1px solid #dee2e6;
                    border-radius: 8px;
                    padding: 1.5rem;
                    text-align: center;
                    transition: all 0.2s ease;
                    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
                }

                .stat-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
                }

                .stat-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 1rem;
                }

                .stat-value {
                    font-size: 1.5rem;
                    font-weight: 600;
                    margin-bottom: 0.5rem;
                    color: #212529;
                }

                .stat-label {
                    color: #6c757d;
                    font-size: 0.875rem;
                    font-weight: 500;
                }

                .card {
                    background: white;
                    border: 1px solid #dee2e6;
                    border-radius: 8px;
                    margin-bottom: 1.5rem;
                    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
                }

                .card-body {
                    padding: 1.5rem;
                }

                .customer-form {
                    background: white;
                    border: 1px solid #dee2e6;
                    border-radius: 8px;
                    padding: 1.5rem;
                    margin-top: 1rem;
                }

                .form-group {
                    margin-bottom: 1rem;
                }

                .form-label {
                    display: block;
                    margin-bottom: 0.5rem;
                    font-weight: 500;
                    color: #495057;
                    font-size: 0.875rem;
                }

                .form-control {
                    width: 100%;
                    padding: 0.625rem 0.75rem;
                    border: 1px solid #ced4da;
                    border-radius: 4px;
                    font-size: 0.875rem;
                    line-height: 1.5;
                    color: #495057;
                    background: white;
                    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
                }

                .form-control:focus {
                    outline: none;
                    border-color: #80bdff;
                    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
                }

                .form-actions {
                    display: flex;
                    gap: 0.75rem;
                    margin-top: 1.5rem;
                }

                .form-button {
                    flex: 1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                    padding: 0.75rem 1rem;
                    border: 1px solid;
                    border-radius: 6px;
                    font-weight: 500;
                    font-size: 0.875rem;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .form-button-success {
                    background: #28a745;
                    border-color: #28a745;
                    color: white;
                }

                .form-button-success:hover {
                    background: #218838;
                    border-color: #1e7e34;
                }

                .form-button-secondary {
                    background: #6c757d;
                    border-color: #6c757d;
                    color: white;
                }

                .form-button-secondary:hover {
                    background: #5a6268;
                    border-color: #545b62;
                }

                .alert-warning-custom {
                    background: #fff3cd;
                    border: 1px solid #ffeaa7;
                    border-radius: 6px;
                    padding: 0.75rem 1rem;
                    margin-bottom: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    color: #856404;
                }

                /* PDF Container Styles */
                .pdf-container {
                    background: white;
                    border: 1px solid #dee2e6;
                    border-radius: 8px;
                    margin-bottom: 1.5rem;
                    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
                    overflow: hidden;
                }

                .pdf-toolbar {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1rem;
                    background: #f8f9fa;
                    border-bottom: 1px solid #dee2e6;
                    flex-wrap: wrap;
                    gap: 0.5rem;
                }

                .pdf-toolbar-section {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .pdf-viewer-container {
                    height: 80vh;
                    min-height: 600px;
                }

                /* Override react-pdf-viewer styles */
                .rpv-core__viewer {
                    border: none;
                    background: #f8f9fa;
                }

                .rpv-core__page-layer {
                    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
                    margin-bottom: 1rem;
                }

                .rpv-core__inner-pages {
                    background: #f8f9fa;
                    padding: 1rem;
                }

                .rpv-zoom__popover-target,
                .rpv-page-navigation__current-page-input,
                button[data-testid] {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0.5rem;
                    border: 1px solid #dee2e6;
                    border-radius: 6px;
                    background: white;
                    color: #495057;
                    font-size: 0.875rem;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    min-width: 36px;
                    height: 36px;
                }

                .rpv-zoom__popover-target:hover,
                button[data-testid]:hover:not(:disabled) {
                    background: #e9ecef;
                    border-color: #adb5bd;
                }

                button[data-testid]:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .gdpr-notice {
                    text-align: center;
                    padding: 2rem 1rem;
                    margin-top: 2rem;
                    color: #6c757d;
                    font-size: 0.875rem;
                    line-height: 1.5;
                }

                @media (max-width: 768px) {
                    .public-quote-container {
                        padding: 1rem;
                    }

                    .quote-header-card {
                        padding: 1.5rem;
                    }

                    .quote-title {
                        font-size: 1.5rem;
                    }

                    .header-content {
                        flex-direction: column;
                        gap: 1rem;
                    }

                    .header-actions {
                        width: 100%;
                        justify-content: stretch;
                    }

                    .header-action-button {
                        flex: 1;
                        justify-content: center;
                        font-size: 0.8rem;
                        padding: 0.65rem 0.75rem;
                    }

                    .stats-grid {
                        grid-template-columns: 1fr;
                    }

                    .pdf-toolbar {
                        flex-direction: column;
                        gap: 1rem;
                    }

                    .pdf-viewer-container {
                        height: 60vh;
                        min-height: 400px;
                    }
                }
            `}</style>

            <div className="public-quote-container">
                {/* Quote Header */}
                <div className="quote-header-card">
                    <div className="header-content">
                        <div className="header-info">
                            <h1 className="quote-title">
                                Offert #{quote?.friendly_id || quote?.quote_number}
                            </h1>

                            <p className="quote-subtitle">
                                från {company?.company_name}
                            </p>

                            <div className={`status-badge ${quote?.status === 'Accepted' ? 'status-accepted' :
                                quote?.status === 'Rejected' ? 'status-rejected' :
                                    isExpired ? 'status-expired' : 'status-pending'
                                }`}>
                                {quote?.status === 'Accepted' ? (
                                    <>
                                        <CheckCircle size={16} />
                                        Accepterad
                                    </>
                                ) : quote?.status === 'Rejected' ? (
                                    <>
                                        <XCircle size={16} />
                                        Avvisad
                                    </>
                                ) : isExpired ? (
                                    <>
                                        <Clock size={16} />
                                        Utgången
                                    </>
                                ) : (
                                    <>
                                        <Eye size={16} />
                                        Väntar på svar
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Action Buttons in Header */}
                        {canRespond && !showCustomerForm && (
                            <div className="header-actions">
                                <button
                                    className="header-action-button header-action-button-success"
                                    onClick={() => handleQuoteAction('accept')}
                                    disabled={actionLoading}
                                >
                                    <CheckCircle size={16} />
                                    Acceptera
                                </button>

                                <button
                                    className="header-action-button header-action-button-danger"
                                    onClick={() => handleQuoteAction('reject')}
                                    disabled={actionLoading}
                                >
                                    <XCircle size={16} />
                                    Avvisa
                                </button>
                            </div>
                        )}
                    </div>

                    {isExpired && (
                        <div className="alert-warning-custom">
                            <AlertTriangle size={20} />
                            <strong>Denna offert har gått ut</strong>
                        </div>
                    )}

                    {/* Customer Form */}
                    {showCustomerForm && (
                        <div className="customer-form">
                            <h6 className="mb-3">Bekräfta dina uppgifter</h6>
                            <p className="text-muted small mb-3">
                                För att acceptera offerten behöver vi dina kontaktuppgifter.
                            </p>

                            <div className="row">
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label className="form-label">Namn *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={customerInfo.name}
                                            onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                                            placeholder="Ditt fullständiga namn"
                                        />
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label className="form-label">E-post *</label>
                                        <input
                                            type="email"
                                            className="form-control"
                                            value={customerInfo.email}
                                            onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                                            placeholder="din@epost.se"
                                        />
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label className="form-label">Telefon</label>
                                        <input
                                            type="tel"
                                            className="form-control"
                                            value={customerInfo.phone}
                                            onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                                            placeholder="070-123 45 67"
                                        />
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label className="form-label">Företag</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={customerInfo.company}
                                            onChange={(e) => setCustomerInfo({ ...customerInfo, company: e.target.value })}
                                            placeholder="Företagsnamn (valfritt)"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-actions">
                                <button
                                    className="form-button form-button-success"
                                    onClick={() => handleQuoteAction('accept')}
                                    disabled={actionLoading}
                                >
                                    {actionLoading ? (
                                        <>
                                            <div className="spinner-border spinner-border-sm" />
                                            Accepterar...
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircle size={16} />
                                            Bekräfta och acceptera
                                        </>
                                    )}
                                </button>

                                <button
                                    className="form-button form-button-secondary"
                                    onClick={() => setShowCustomerForm(false)}
                                    disabled={actionLoading}
                                >
                                    Tillbaka
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Statistics Cards */}
                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-icon" style={{ background: '#e3f2fd', color: '#1976d2' }}>
                            <DollarSign size={24} />
                        </div>
                        <div className="stat-value" style={{ color: '#1976d2' }}>
                            {formatCurrency(calculateTotal())}
                        </div>
                        <div className="stat-label">Totalt belopp</div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon" style={{ background: '#f3e5f5', color: '#7b1fa2' }}>
                            <FileText size={24} />
                        </div>
                        <div className="stat-value" style={{ color: '#7b1fa2' }}>
                            {quote?.items?.length || 0}
                        </div>
                        <div className="stat-label">Antal artiklar</div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon" style={{ background: '#fff3e0', color: '#f57c00' }}>
                            <Calendar size={24} />
                        </div>
                        <div className="stat-value" style={{ color: '#f57c00' }}>
                            {new Date(quote?.quote_date).toLocaleDateString('sv-SE')}
                        </div>
                        <div className="stat-label">Datum</div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon" style={{
                            background: daysUntilExpiry > 0 ? '#e8f5e8' : '#ffebee',
                            color: daysUntilExpiry > 0 ? '#388e3c' : '#d32f2f'
                        }}>
                            <Clock size={24} />
                        </div>
                        <div className="stat-value" style={{
                            color: daysUntilExpiry > 0 ? '#388e3c' : '#d32f2f'
                        }}>
                            {daysUntilExpiry > 0 ? daysUntilExpiry : 0}
                        </div>
                        <div className="stat-label">Dagar kvar</div>
                    </div>
                </div>

                {/* PDF Display with @react-pdf-viewer */}
                <div className="pdf-container">
                    {pdfUrl && (
                        <>
                            <div className="pdf-toolbar">
                                <div className="pdf-toolbar-section">
                                    <GoToPreviousPageButton />
                                    <div style={{ padding: '0 0.5rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                        <CurrentPageInput /> / <NumberOfPages />
                                    </div>
                                    <GoToNextPageButton />
                                </div>

                                <div className="pdf-toolbar-section">
                                    <ZoomOutButton />
                                    <ZoomPopover />
                                    <ZoomInButton />
                                    <PrintButton />
                                    <button
                                        className="header-action-button"
                                        onClick={handleDownloadPDF}
                                        title="Ladda ner PDF"
                                        style={{ padding: '0.5rem', minWidth: '36px', height: '36px' }}
                                    >
                                        <Download size={16} />
                                    </button>
                                </div>
                            </div>

                            <div className="pdf-viewer-container">
                                <Worker workerUrl={`https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js`}>
                                    <Viewer
                                        fileUrl={pdfUrl}
                                        plugins={[
                                            zoomPluginInstance,
                                            pageNavigationPluginInstance,
                                            printPluginInstance
                                        ]}
                                        defaultScale={SpecialZoomLevel.PageFit}
                                    />
                                </Worker>
                            </div>
                        </>
                    )}
                </div>

                {/* Notes - Only show if there are notes */}
                {quote?.notes && (
                    <div className="card">
                        <div className="card-body">
                            <h6 className="mb-3 fw-semibold">
                                <FileText size={20} className="me-2" />
                                Meddelande
                            </h6>
                            <p className="mb-0">{quote.notes}</p>
                        </div>
                    </div>
                )}

                {/* GDPR Notice */}
                <div className="gdpr-notice">
                    <Shield size={20} className="mb-2" style={{ color: '#6c757d' }} />
                    <p className="mb-0">
                        Din information behandlas säkert och skyddas enligt GDPR.
                        Vi delar aldrig dina uppgifter med tredje part utan ditt samtycke.
                    </p>
                </div>
            </div>
        </>
    );
};

export default PublicQuoteView;





// import React, { useState, useEffect } from 'react';
// import { useParams } from 'react-router-dom';
// import publicAxios from '../../../../services/PublicAxiosInstance.js';
// import { getApiUrl } from '../../../../utils/ApiUtils.js';
// import swal from 'sweetalert';
// import {
//     CheckCircle,
//     XCircle,
//     Clock,
//     FileText,
//     DollarSign,
//     Calendar,
//     Building,
//     User,
//     Mail,
//     Phone,
//     MapPin,
//     Download,
//     Eye,
//     AlertTriangle,
//     Shield,
//     ExternalLink
// } from 'lucide-react';

// const PublicQuoteView = () => {
//     const { companyId, token } = useParams();

//     const [quote, setQuote] = useState(null);
//     const [company, setCompany] = useState(null);
//     const [loading, setLoading] = useState(true);
//     const [error, setError] = useState(null);
//     const [actionLoading, setActionLoading] = useState(false);
//     const [customerInfo, setCustomerInfo] = useState({
//         name: '',
//         email: '',
//         phone: '',
//         company: ''
//     });
//     const [showCustomerForm, setShowCustomerForm] = useState(false);
//     const [pdfUrl, setPdfUrl] = useState(null);

//     useEffect(() => {
//         fetchQuoteDetails();
//     }, [token]);

//     const fetchQuoteDetails = async () => {
//         try {
//             setLoading(true);
//             const apiUrl = getApiUrl(`${companyId}/public/quote/view/${token}`);
//             console.log('Fetching quote from URL:', apiUrl);

//             const response = await publicAxios.get(apiUrl);

//             console.log('Quote response:', response.data);

//             if (response.data.success) {
//                 setQuote(response.data.quote);
//                 setCompany(response.data.company);

//                 // Generate PDF URL for embedding
//                 const pdfApiUrl = getApiUrl(`${companyId}/public/quote/pdf/${token}`);
//                 console.log('PDF API URL:', pdfApiUrl);
//                 setPdfUrl(pdfApiUrl);

//                 // Pre-populate customer info if available
//                 if (response.data.quote.customer) {
//                     setCustomerInfo({
//                         name: response.data.quote.customer.name || '',
//                         email: response.data.quote.customer.email || '',
//                         phone: response.data.quote.customer.phone || '',
//                         company: response.data.quote.customer.company_name || ''
//                     });
//                 }
//             } else {
//                 setError('Offerten kunde inte hittas eller har gått ut.');
//             }
//         } catch (error) {
//             console.error('Error fetching quote:', error);
//             if (error.response?.status === 404) {
//                 setError('Offerten kunde inte hittas.');
//             } else if (error.response?.status === 410) {
//                 setError('Denna offert har gått ut eller är inte längre tillgänglig.');
//             } else if (error.response?.status === 403) {
//                 setError('Du har inte behörighet att visa denna offert.');
//             } else {
//                 setError('Ett fel uppstod när offerten skulle hämtas.');
//             }
//         } finally {
//             setLoading(false);
//         }
//     };

//     const handleQuoteAction = async (action) => {
//         // Show customer info form for acceptance
//         if (action === 'accept' && !showCustomerForm) {
//             setShowCustomerForm(true);
//             return;
//         }

//         // Validate customer info for acceptance
//         if (action === 'accept') {
//             if (!customerInfo.name || !customerInfo.email) {
//                 swal('Saknad information', 'Vänligen fyll i namn och e-post.', 'warning');
//                 return;
//             }
//         }

//         try {
//             setActionLoading(true);

//             const response = await publicAxios.post(getApiUrl(`${companyId}/public/quote/action/${token}`), {
//                 action,
//                 customer_info: action === 'accept' ? customerInfo : null
//             });

//             if (response.data.success) {
//                 if (action === 'accept') {
//                     swal('Tack!', 'Offerten har accepterats. Vi kommer att kontakta dig inom kort.', 'success');
//                     if (response.data.invoice_created) {
//                         swal('Faktura skapad', 'En faktura har automatiskt skapats baserat på den accepterade offerten.', 'info');
//                     }
//                 } else {
//                     swal('Tack för din feedback', 'Offerten har markerats som avvisad.', 'info');
//                 }

//                 // Update quote status
//                 setQuote(prev => ({ ...prev, status: action === 'accept' ? 'Accepted' : 'Rejected' }));
//                 setShowCustomerForm(false);
//             }
//         } catch (error) {
//             console.error('Error handling quote action:', error);
//             swal('Fel', 'Ett fel uppstod. Vänligen försök igen.', 'error');
//         } finally {
//             setActionLoading(false);
//         }
//     };

//     const handleDownloadPDF = async () => {
//         try {
//             const response = await publicAxios.get(getApiUrl(`${companyId}/public/quote/pdf/${token}`), {
//                 responseType: 'blob'
//             });

//             const blob = new Blob([response.data], { type: 'application/pdf' });
//             const url = window.URL.createObjectURL(blob);
//             const link = document.createElement('a');
//             link.href = url;
//             link.download = `Offert_${quote?.friendly_id || quote?.quote_number}.pdf`;
//             document.body.appendChild(link);
//             link.click();
//             document.body.removeChild(link);
//             window.URL.revokeObjectURL(url);
//         } catch (error) {
//             console.error('Error downloading PDF:', error);
//             swal('Fel', 'Kunde inte ladda ner PDF:en. Vänligen försök igen.', 'error');
//         }
//     };

//     const calculateTotal = () => {
//         if (!quote?.items) return 0;

//         return quote.items.reduce((total, item) => {
//             const itemTotal = (item.unitprice * item.quantity);
//             const taxRate = parseFloat(item.tax_rate?.toString().replace('%', '')) || 0;
//             const taxAmount = itemTotal * (taxRate / 100);
//             return total + itemTotal + taxAmount;
//         }, 0);
//     };

//     const formatCurrency = (amount) => {
//         return new Intl.NumberFormat('sv-SE', {
//             style: 'currency',
//             currency: quote?.currency || 'SEK'
//         }).format(amount);
//     };

//     if (loading) {
//         return (
//             <div className="min-vh-100 d-flex justify-content-center align-items-center bg-light">
//                 <div className="text-center">
//                     <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
//                         <span className="visually-hidden">Laddar...</span>
//                     </div>
//                     <h5 className="text-muted">Laddar offert...</h5>
//                 </div>
//             </div>
//         );
//     }

//     if (error) {
//         return (
//             <div className="min-vh-100 d-flex justify-content-center align-items-center bg-light">
//                 <div className="text-center">
//                     <div className="mb-4">
//                         <AlertTriangle size={64} className="text-warning mb-3" />
//                         <h3 className="h4 text-dark mb-3">Offerten kunde inte visas</h3>
//                         <p className="text-muted mb-4">{error}</p>
//                     </div>
//                 </div>
//             </div>
//         );
//     }

//     const isExpired = new Date(quote?.valid_until) < new Date();
//     const canRespond = quote?.status === 'Sent' && !isExpired;
//     const daysUntilExpiry = Math.ceil((new Date(quote?.valid_until) - new Date()) / (1000 * 60 * 60 * 24));

//     return (
//         <>
//             <style>{`
//                 .public-quote-container {
//                     max-width: 1200px;
//                     margin: 0 auto;
//                     padding: 2rem 1rem;
//                     background: #f8f9fa;
//                     min-height: 100vh;
//                 }

//                 .quote-header-card {
//                     background: white;
//                     border: 1px solid #dee2e6;
//                     border-radius: 8px;
//                     padding: 2rem;
//                     margin-bottom: 1.5rem;
//                     box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
//                 }

//                 .quote-title {
//                     font-size: 1.75rem;
//                     font-weight: 600;
//                     margin-bottom: 0.5rem;
//                     color: #212529;
//                 }

//                 .quote-subtitle {
//                     color: #6c757d;
//                     font-size: 1rem;
//                     margin-bottom: 1rem;
//                 }

//                 .header-content {
//                     display: flex;
//                     justify-content: space-between;
//                     align-items: flex-start;
//                     gap: 2rem;
//                     margin-bottom: 1.5rem;
//                 }

//                 .header-info {
//                     flex: 1;
//                 }

//                 .header-actions {
//                     display: flex;
//                     gap: 0.75rem;
//                     flex-shrink: 0;
//                 }

//                 .status-badge {
//                     display: inline-flex;
//                     align-items: center;
//                     gap: 0.5rem;
//                     padding: 0.5rem 1rem;
//                     border-radius: 6px;
//                     font-weight: 500;
//                     font-size: 0.875rem;
//                     margin-bottom: 1rem;
//                 }

//                 .status-accepted { 
//                     background: #d1edff; 
//                     color: #004085; 
//                     border: 1px solid #b6d7ff; 
//                 }
//                 .status-rejected { 
//                     background: #f8d7da; 
//                     color: #721c24; 
//                     border: 1px solid #f5c6cb; 
//                 }
//                 .status-expired { 
//                     background: #e2e3e5; 
//                     color: #383d41; 
//                     border: 1px solid #d6d8db; 
//                 }
//                 .status-pending { 
//                     background: #fff3cd; 
//                     color: #856404; 
//                     border: 1px solid #ffeaa7; 
//                 }

//                 /* Professional action buttons - compact header style */
//                 .header-action-button {
//                     display: flex;
//                     align-items: center;
//                     gap: 0.5rem;
//                     padding: 0.75rem 1.25rem;
//                     background: linear-gradient(135deg, #fff 0%, #f8fafc 100%);
//                     border: 1px solid #e2e8f0;
//                     border-radius: 8px;
//                     text-decoration: none;
//                     color: #374151;
//                     font-weight: 500;
//                     font-size: 0.875rem;
//                     transition: all 0.2s ease;
//                     cursor: pointer;
//                     white-space: nowrap;
//                 }

//                 .header-action-button:hover {
//                     transform: translateY(-1px);
//                     box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
//                     border-color: #3b82f6;
//                     color: #3b82f6;
//                     text-decoration: none;
//                 }

//                 .header-action-button-success {
//                     background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
//                     border-color: #10b981;
//                     color: #059669;
//                 }

//                 .header-action-button-success:hover {
//                     border-color: #059669;
//                     color: #047857;
//                     box-shadow: 0 4px 12px rgba(16, 185, 129, 0.15);
//                 }

//                 .header-action-button-danger {
//                     background: linear-gradient(135deg, #fef2f2 0%, #fecaca 100%);
//                     border-color: #ef4444;
//                     color: #dc2626;
//                 }

//                 .header-action-button-danger:hover {
//                     border-color: #dc2626;
//                     color: #b91c1c;
//                     box-shadow: 0 4px 12px rgba(239, 68, 68, 0.15);
//                 }

//                 .stats-grid {
//                     display: grid;
//                     grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
//                     gap: 1rem;
//                     margin-bottom: 1.5rem;
//                 }

//                 .stat-card {
//                     background: white;
//                     border: 1px solid #dee2e6;
//                     border-radius: 8px;
//                     padding: 1.5rem;
//                     text-align: center;
//                     transition: all 0.2s ease;
//                     box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
//                 }

//                 .stat-card:hover {
//                     transform: translateY(-2px);
//                     box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
//                 }

//                 .stat-icon {
//                     width: 48px;
//                     height: 48px;
//                     border-radius: 8px;
//                     display: flex;
//                     align-items: center;
//                     justify-content: center;
//                     margin: 0 auto 1rem;
//                 }

//                 .stat-value {
//                     font-size: 1.5rem;
//                     font-weight: 600;
//                     margin-bottom: 0.5rem;
//                     color: #212529;
//                 }

//                 .stat-label {
//                     color: #6c757d;
//                     font-size: 0.875rem;
//                     font-weight: 500;
//                 }

//                 .card {
//                     background: white;
//                     border: 1px solid #dee2e6;
//                     border-radius: 8px;
//                     margin-bottom: 1.5rem;
//                     box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
//                 }

//                 .card-body {
//                     padding: 1.5rem;
//                 }

//                 .customer-form {
//                     background: white;
//                     border: 1px solid #dee2e6;
//                     border-radius: 8px;
//                     padding: 1.5rem;
//                     margin-top: 1rem;
//                 }

//                 .form-group {
//                     margin-bottom: 1rem;
//                 }

//                 .form-label {
//                     display: block;
//                     margin-bottom: 0.5rem;
//                     font-weight: 500;
//                     color: #495057;
//                     font-size: 0.875rem;
//                 }

//                 .form-control {
//                     width: 100%;
//                     padding: 0.625rem 0.75rem;
//                     border: 1px solid #ced4da;
//                     border-radius: 4px;
//                     font-size: 0.875rem;
//                     line-height: 1.5;
//                     color: #495057;
//                     background: white;
//                     transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
//                 }

//                 .form-control:focus {
//                     outline: none;
//                     border-color: #80bdff;
//                     box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
//                 }

//                 .form-actions {
//                     display: flex;
//                     gap: 0.75rem;
//                     margin-top: 1.5rem;
//                 }

//                 .form-button {
//                     flex: 1;
//                     display: flex;
//                     align-items: center;
//                     justify-content: center;
//                     gap: 0.5rem;
//                     padding: 0.75rem 1rem;
//                     border: 1px solid;
//                     border-radius: 6px;
//                     font-weight: 500;
//                     font-size: 0.875rem;
//                     cursor: pointer;
//                     transition: all 0.2s ease;
//                 }

//                 .form-button-success {
//                     background: #28a745;
//                     border-color: #28a745;
//                     color: white;
//                 }

//                 .form-button-success:hover {
//                     background: #218838;
//                     border-color: #1e7e34;
//                 }

//                 .form-button-secondary {
//                     background: #6c757d;
//                     border-color: #6c757d;
//                     color: white;
//                 }

//                 .form-button-secondary:hover {
//                     background: #5a6268;
//                     border-color: #545b62;
//                 }

//                 .alert-warning-custom {
//                     background: #fff3cd;
//                     border: 1px solid #ffeaa7;
//                     border-radius: 6px;
//                     padding: 0.75rem 1rem;
//                     margin-bottom: 1rem;
//                     display: flex;
//                     align-items: center;
//                     gap: 0.5rem;
//                     color: #856404;
//                 }

//                 .pdf-container {
//                     background: transparent;
//                     border: none;
//                     border-radius: 0;
//                     overflow: visible;
//                     margin-bottom: 1.5rem;
//                     box-shadow: none;
//                 }

//                 .pdf-embed-fullpage {
//                     width: 100%;
//                     min-height: 1200px;
//                     height: auto;
//                     border: none;
//                     background: transparent;
//                     display: block;
//                 }

//                 /* Hide PDF viewer UI completely */
//                 .pdf-clean {
//                     width: 100%;
//                     min-height: 1200px;
//                     border: none;
//                     background: white;
//                     box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
//                     border-radius: 8px;
//                     overflow: visible;
//                 }

//                 .pdf-object {
//                     width: 100%;
//                     min-height: 1200px;
//                     border: none;
//                     background: white;
//                     border-radius: 8px;
//                     box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
//                 }

//                 .gdpr-notice {
//                     text-align: center;
//                     padding: 2rem 1rem;
//                     margin-top: 2rem;
//                     color: #6c757d;
//                     font-size: 0.875rem;
//                     line-height: 1.5;
//                 }

//                 @media (max-width: 768px) {
//                     .public-quote-container {
//                         padding: 1rem;
//                     }

//                     .quote-header-card {
//                         padding: 1.5rem;
//                     }

//                     .quote-title {
//                         font-size: 1.5rem;
//                     }

//                     .header-content {
//                         flex-direction: column;
//                         gap: 1rem;
//                     }

//                     .header-actions {
//                         width: 100%;
//                         justify-content: stretch;
//                     }

//                     .header-action-button {
//                         flex: 1;
//                         justify-content: center;
//                         font-size: 0.8rem;
//                         padding: 0.65rem 0.75rem;
//                     }

//                     .stats-grid {
//                         grid-template-columns: 1fr;
//                     }

//                     .pdf-embed {
//                         height: 600px;
//                     }
//                 }
//             `}</style>

//             <div className="public-quote-container">
//                 {/* Quote Header */}
//                 <div className="quote-header-card">
//                     <div className="header-content">
//                         <div className="header-info">
//                             <h1 className="quote-title">
//                                 Offert #{quote?.friendly_id || quote?.quote_number}
//                             </h1>

//                             <p className="quote-subtitle">
//                                 från {company?.company_name}
//                             </p>

//                             <div className={`status-badge ${quote?.status === 'Accepted' ? 'status-accepted' :
//                                 quote?.status === 'Rejected' ? 'status-rejected' :
//                                     isExpired ? 'status-expired' : 'status-pending'
//                                 }`}>
//                                 {quote?.status === 'Accepted' ? (
//                                     <>
//                                         <CheckCircle size={16} />
//                                         Accepterad
//                                     </>
//                                 ) : quote?.status === 'Rejected' ? (
//                                     <>
//                                         <XCircle size={16} />
//                                         Avvisad
//                                     </>
//                                 ) : isExpired ? (
//                                     <>
//                                         <Clock size={16} />
//                                         Utgången
//                                     </>
//                                 ) : (
//                                     <>
//                                         <Eye size={16} />
//                                         Väntar på svar
//                                     </>
//                                 )}
//                             </div>
//                         </div>

//                         {/* Action Buttons in Header */}
//                         {canRespond && !showCustomerForm && (
//                             <div className="header-actions">
//                                 <button
//                                     className="header-action-button header-action-button-success"
//                                     onClick={() => handleQuoteAction('accept')}
//                                     disabled={actionLoading}
//                                 >
//                                     <CheckCircle size={16} />
//                                     Acceptera
//                                 </button>

//                                 <button
//                                     className="header-action-button header-action-button-danger"
//                                     onClick={() => handleQuoteAction('reject')}
//                                     disabled={actionLoading}
//                                 >
//                                     <XCircle size={16} />
//                                     Avvisa
//                                 </button>
//                             </div>
//                         )}
//                     </div>

//                     {isExpired && (
//                         <div className="alert-warning-custom">
//                             <AlertTriangle size={20} />
//                             <strong>Denna offert har gått ut</strong>
//                         </div>
//                     )}

//                     {/* Customer Form */}
//                     {showCustomerForm && (
//                         <div className="customer-form">
//                             <h6 className="mb-3">Bekräfta dina uppgifter</h6>
//                             <p className="text-muted small mb-3">
//                                 För att acceptera offerten behöver vi dina kontaktuppgifter.
//                             </p>

//                             <div className="row">
//                                 <div className="col-md-6">
//                                     <div className="form-group">
//                                         <label className="form-label">Namn *</label>
//                                         <input
//                                             type="text"
//                                             className="form-control"
//                                             value={customerInfo.name}
//                                             onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
//                                             placeholder="Ditt fullständiga namn"
//                                         />
//                                     </div>
//                                 </div>

//                                 <div className="col-md-6">
//                                     <div className="form-group">
//                                         <label className="form-label">E-post *</label>
//                                         <input
//                                             type="email"
//                                             className="form-control"
//                                             value={customerInfo.email}
//                                             onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
//                                             placeholder="din@epost.se"
//                                         />
//                                     </div>
//                                 </div>

//                                 <div className="col-md-6">
//                                     <div className="form-group">
//                                         <label className="form-label">Telefon</label>
//                                         <input
//                                             type="tel"
//                                             className="form-control"
//                                             value={customerInfo.phone}
//                                             onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
//                                             placeholder="070-123 45 67"
//                                         />
//                                     </div>
//                                 </div>

//                                 <div className="col-md-6">
//                                     <div className="form-group">
//                                         <label className="form-label">Företag</label>
//                                         <input
//                                             type="text"
//                                             className="form-control"
//                                             value={customerInfo.company}
//                                             onChange={(e) => setCustomerInfo({ ...customerInfo, company: e.target.value })}
//                                             placeholder="Företagsnamn (valfritt)"
//                                         />
//                                     </div>
//                                 </div>
//                             </div>

//                             <div className="form-actions">
//                                 <button
//                                     className="form-button form-button-success"
//                                     onClick={() => handleQuoteAction('accept')}
//                                     disabled={actionLoading}
//                                 >
//                                     {actionLoading ? (
//                                         <>
//                                             <div className="spinner-border spinner-border-sm" />
//                                             Accepterar...
//                                         </>
//                                     ) : (
//                                         <>
//                                             <CheckCircle size={16} />
//                                             Bekräfta och acceptera
//                                         </>
//                                     )}
//                                 </button>

//                                 <button
//                                     className="form-button form-button-secondary"
//                                     onClick={() => setShowCustomerForm(false)}
//                                     disabled={actionLoading}
//                                 >
//                                     Tillbaka
//                                 </button>
//                             </div>
//                         </div>
//                     )}
//                 </div>

//                 {/* Statistics Cards */}
//                 <div className="stats-grid">
//                     <div className="stat-card">
//                         <div className="stat-icon" style={{ background: '#e3f2fd', color: '#1976d2' }}>
//                             <DollarSign size={24} />
//                         </div>
//                         <div className="stat-value" style={{ color: '#1976d2' }}>
//                             {formatCurrency(calculateTotal())}
//                         </div>
//                         <div className="stat-label">Totalt belopp</div>
//                     </div>

//                     <div className="stat-card">
//                         <div className="stat-icon" style={{ background: '#f3e5f5', color: '#7b1fa2' }}>
//                             <FileText size={24} />
//                         </div>
//                         <div className="stat-value" style={{ color: '#7b1fa2' }}>
//                             {quote?.items?.length || 0}
//                         </div>
//                         <div className="stat-label">Antal artiklar</div>
//                     </div>

//                     <div className="stat-card">
//                         <div className="stat-icon" style={{ background: '#fff3e0', color: '#f57c00' }}>
//                             <Calendar size={24} />
//                         </div>
//                         <div className="stat-value" style={{ color: '#f57c00' }}>
//                             {new Date(quote?.quote_date).toLocaleDateString('sv-SE')}
//                         </div>
//                         <div className="stat-label">Datum</div>
//                     </div>

//                     <div className="stat-card">
//                         <div className="stat-icon" style={{
//                             background: daysUntilExpiry > 0 ? '#e8f5e8' : '#ffebee',
//                             color: daysUntilExpiry > 0 ? '#388e3c' : '#d32f2f'
//                         }}>
//                             <Clock size={24} />
//                         </div>
//                         <div className="stat-value" style={{
//                             color: daysUntilExpiry > 0 ? '#388e3c' : '#d32f2f'
//                         }}>
//                             {daysUntilExpiry > 0 ? daysUntilExpiry : 0}
//                         </div>
//                         <div className="stat-label">Dagar kvar</div>
//                     </div>
//                 </div>

//                 {/* PDF Display - Full Width */}
//                 <div className="pdf-container">
//                     {pdfUrl && (
//                         <object
//                             data={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0&statusbar=0&view=FitH&zoom=page-fit`}
//                             type="application/pdf"
//                             className="pdf-object"
//                             style={{
//                                 width: '100%',
//                                 minHeight: '1200px',
//                                 border: 'none',
//                                 background: 'white',
//                                 borderRadius: '8px',
//                                 boxShadow: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)'
//                             }}
//                         >
//                             {/* Fallback for browsers that don't support object tag */}
//                             <iframe
//                                 src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0&statusbar=0&view=FitH&zoom=page-fit`}
//                                 className="pdf-clean"
//                                 title="Offert PDF"
//                                 frameBorder="0"
//                                 scrolling="no"
//                                 style={{
//                                     width: '100%',
//                                     minHeight: '1200px',
//                                     border: 'none'
//                                 }}
//                             />
//                             <div style={{
//                                 padding: '2rem',
//                                 textAlign: 'center',
//                                 background: '#f8f9fa',
//                                 borderRadius: '8px',
//                                 border: '1px solid #dee2e6'
//                             }}>
//                                 <p>PDF kunde inte visas i din webbläsare.</p>
//                                 <a
//                                     href={pdfUrl}
//                                     target="_blank"
//                                     rel="noopener noreferrer"
//                                     style={{
//                                         display: 'inline-flex',
//                                         alignItems: 'center',
//                                         gap: '0.5rem',
//                                         padding: '0.75rem 1.5rem',
//                                         background: '#007bff',
//                                         color: 'white',
//                                         textDecoration: 'none',
//                                         borderRadius: '6px',
//                                         fontWeight: '500'
//                                     }}
//                                 >
//                                     <ExternalLink size={16} />
//                                     Öppna PDF i ny flik
//                                 </a>
//                             </div>
//                         </object>
//                     )}
//                 </div>

//                 {/* Notes - Only show if there are notes */}
//                 {quote?.notes && (
//                     <div className="card">
//                         <div className="card-body">
//                             <h6 className="mb-3 fw-semibold">
//                                 <FileText size={20} className="me-2" />
//                                 Meddelande
//                             </h6>
//                             <p className="mb-0">{quote.notes}</p>
//                         </div>
//                     </div>
//                 )}

//                 {/* GDPR Notice */}
//                 <div className="gdpr-notice">
//                     <Shield size={20} className="mb-2" style={{ color: '#6c757d' }} />
//                     <p className="mb-0">
//                         Din information behandlas säkert och skyddas enligt GDPR.
//                         Vi delar aldrig dina uppgifter med tredje part utan ditt samtycke.
//                     </p>
//                 </div>
//             </div>
//         </>
//     );
// };

// export default PublicQuoteView;